import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(8, "8 caractères minimum"),
});

const registerSchema = z.object({
  cabinetName: z.string().min(2, "Nom du cabinet requis"),
  fullName: z.string().min(2, "Votre nom requis"),
  email: z.string().email("Email invalide"),
  password: z.string().min(8, "8 caractères minimum"),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

type LoginForm = z.infer<typeof loginSchema>;
type RegisterForm = z.infer<typeof registerSchema>;

const AuthCabinet = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const loginForm = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });
  const registerForm = useForm<RegisterForm>({ resolver: zodResolver(registerSchema) });

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleLogin = async (data: LoginForm) => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });
    setLoading(false);
    if (error) showMessage("error", error.message);
    else navigate("/cabinet");
  };

  const handleRegister = async (data: RegisterForm) => {
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          full_name: data.fullName,
          cabinet_name: data.cabinetName,
        },
      },
    });
    setLoading(false);
    if (error) showMessage("error", error.message);
    else {
      showMessage("success", "Compte cabinet créé ! Vérifiez votre email.");
      setTimeout(() => navigate("/cabinet"), 2000);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "#0A0A08",
    border: "1px solid #2A2520",
    borderRadius: "10px",
    padding: "12px 16px",
    color: "#F5F0E8",
    fontSize: "0.9rem",
    fontFamily: "'Sora', sans-serif",
    outline: "none",
    transition: "border-color 0.2s",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.78rem",
    color: "#9A8F7E",
    marginBottom: "6px",
    fontWeight: 600,
  };

  const errorStyle: React.CSSProperties = {
    fontSize: "0.75rem",
    color: "#EF4444",
    marginTop: "4px",
  };

  return (
    <div
      style={{
        fontFamily: "'Sora', 'Segoe UI', sans-serif",
        background: "#0A0A0A",
        minHeight: "100vh",
        display: "flex",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=Space+Mono:wght@400;700&display=swap');
        * { box-sizing: border-box; }
        input:focus { border-color: #3B82F6 !important; }
        input::placeholder { color: #3A3020; }
        .auth-btn {
          width: 100%;
          padding: 13px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
          font-family: 'Sora', sans-serif;
          transition: all 0.3s;
          border: none;
        }
        .auth-btn-primary {
          background: linear-gradient(135deg, #3B82F6, #60A5FA);
          color: white;
        }
        .auth-btn-primary:hover {
          box-shadow: 0 8px 30px rgba(59,130,246,0.4);
          transform: translateY(-1px);
        }
        .auth-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        .tab-btn {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 8px;
          font-family: 'Sora', sans-serif;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
      `}</style>

      {/* Left panel - cabinet spécifique */}
      <div
        style={{
          flex: "0 0 45%",
          background: "linear-gradient(135deg, #060D18 0%, #0F1729 100%)",
          borderRight: "1px solid #1E293B",
          padding: "60px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)",
            top: "-100px",
            left: "-100px",
            pointerEvents: "none",
          }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: "10px", position: "relative" }}>
          <div
            style={{
              width: 38,
              height: 38,
              background: "linear-gradient(135deg, #3B82F6, #60A5FA)",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "1.1rem",
              color: "white",
            }}
          >
            W
          </div>
          <span style={{ fontWeight: 800, fontSize: "1.2rem", color: "#F5F0E8" }}>
            Woahda<span style={{ color: "#60A5FA" }}>.</span>
          </span>
        </div>

        <div style={{ position: "relative" }}>
          <div
            style={{
              fontSize: "0.72rem",
              color: "#60A5FA",
              fontWeight: 700,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              marginBottom: "20px",
              fontFamily: "'Space Mono', monospace",
            }}
          >
            ESPACE CABINET
          </div>
          <h1
            style={{
              fontSize: "2.6rem",
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: "#F5F0E8",
              marginBottom: "20px",
            }}
          >
            Pour les cabinets
            <br />
            <span
              style={{
                background: "linear-gradient(135deg, #3B82F6, #60A5FA)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              d'expertise
            </span>
          </h1>
          <p style={{ color: "#94A3B8", fontSize: "0.95rem", lineHeight: 1.7, maxWidth: "360px" }}>
            Gérez vos dossiers clients, invitez-les à accéder à leurs transactions et accompagnez-les sur la plateforme Woahda Compta.
          </p>
        </div>

        <div style={{ position: "relative" }}>
          {[
            { icon: "📁", text: "Dossiers clients illimités" },
            { icon: "✉️", text: "Invitations par email" },
            { icon: "🔐", text: "Accès sécurisé par dossier" },
          ].map((f, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "12px",
                color: "#94A3B8",
                fontSize: "0.85rem",
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  background: "rgba(59,130,246,0.15)",
                  border: "1px solid rgba(59,130,246,0.3)",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.9rem",
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </span>
              {f.text}
            </div>
          ))}
        </div>
      </div>

      {/* Right panel - Form */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px",
        }}
      >
        <div style={{ width: "100%", maxWidth: "420px" }}>
          {message && (
            <div
              style={{
                background: message.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                border: `1px solid ${message.type === "success" ? "#22C55E40" : "#EF444440"}`,
                borderRadius: "10px",
                padding: "12px 16px",
                fontSize: "0.85rem",
                color: message.type === "success" ? "#22C55E" : "#EF4444",
                marginBottom: "20px",
              }}
            >
              {message.type === "success" ? "✓" : "⚠"} {message.text}
            </div>
          )}

          <div
            style={{
              display: "flex",
              background: "#111109",
              border: "1px solid #1E1C14",
              borderRadius: "12px",
              padding: "4px",
              marginBottom: "28px",
            }}
          >
            {(["login", "register"] as const).map((tab) => (
              <button
                key={tab}
                className="tab-btn"
                onClick={() => { setMode(tab); setMessage(null); }}
                style={{
                  background: mode === tab ? "linear-gradient(135deg, #3B82F6, #60A5FA)" : "transparent",
                  color: mode === tab ? "white" : "#5A5040",
                }}
              >
                {tab === "login" ? "Connexion cabinet" : "Créer un cabinet"}
              </button>
            ))}
          </div>

          {mode === "login" && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)}>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Email du cabinet</label>
                <input
                  {...loginForm.register("email")}
                  style={inputStyle}
                  placeholder="cabinet@expertise.com"
                  type="email"
                />
                {loginForm.formState.errors.email && (
                  <p style={errorStyle}>{loginForm.formState.errors.email.message}</p>
                )}
              </div>
              <div style={{ marginBottom: "24px" }}>
                <label style={labelStyle}>Mot de passe</label>
                <input
                  {...loginForm.register("password")}
                  style={inputStyle}
                  placeholder="••••••••"
                  type="password"
                />
                {loginForm.formState.errors.password && (
                  <p style={errorStyle}>{loginForm.formState.errors.password.message}</p>
                )}
              </div>
              <button className="auth-btn auth-btn-primary" type="submit" disabled={loading}>
                {loading ? "Connexion..." : "Accéder à mon espace cabinet →"}
              </button>
            </form>
          )}

          {mode === "register" && (
            <form onSubmit={registerForm.handleSubmit(handleRegister)}>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Nom du cabinet</label>
                <input
                  {...registerForm.register("cabinetName")}
                  style={inputStyle}
                  placeholder="Cabinet Koné & Associés"
                />
                {registerForm.formState.errors.cabinetName && (
                  <p style={errorStyle}>{registerForm.formState.errors.cabinetName.message}</p>
                )}
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Votre nom</label>
                <input
                  {...registerForm.register("fullName")}
                  style={inputStyle}
                  placeholder="Koné Amadou"
                />
                {registerForm.formState.errors.fullName && (
                  <p style={errorStyle}>{registerForm.formState.errors.fullName.message}</p>
                )}
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Adresse email</label>
                <input
                  {...registerForm.register("email")}
                  style={inputStyle}
                  placeholder="cabinet@expertise.com"
                  type="email"
                />
                {registerForm.formState.errors.email && (
                  <p style={errorStyle}>{registerForm.formState.errors.email.message}</p>
                )}
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Mot de passe</label>
                <input
                  {...registerForm.register("password")}
                  style={inputStyle}
                  placeholder="••••••••"
                  type="password"
                />
                {registerForm.formState.errors.password && (
                  <p style={errorStyle}>{registerForm.formState.errors.password.message}</p>
                )}
              </div>
              <div style={{ marginBottom: "24px" }}>
                <label style={labelStyle}>Confirmer le mot de passe</label>
                <input
                  {...registerForm.register("confirmPassword")}
                  style={inputStyle}
                  placeholder="••••••••"
                  type="password"
                />
                {registerForm.formState.errors.confirmPassword && (
                  <p style={errorStyle}>{registerForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>
              <button className="auth-btn auth-btn-primary" type="submit" disabled={loading}>
                {loading ? "Création..." : "Créer mon espace cabinet →"}
              </button>
            </form>
          )}

          <p
            style={{
              textAlign: "center",
              fontSize: "0.78rem",
              color: "#5A5040",
              marginTop: "24px",
            }}
          >
            <a href="/auth" style={{ color: "#3B82F6", textDecoration: "none" }}>
              ← Connexion PME / client
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthCabinet;
